import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { routeTo } from "@/app/routes";

import { useBrand } from "@/core/brand/brand-context";
import { formatSlots } from "@/core/display/slots";
import {
  formatBps,
  formatCount,
  formatCountOf,
  formatDay,
  formatDayLong,
  formatPaise,
} from "@/core/format/format";
import { displayName } from "@/features/subscriptions/subscription-display";
import { useCustomerPlans } from "@/features/subscriptions/subscriptions.queries";
import { DietMark } from "@/features/dishes/DietMark";
import { Button } from "@/ui/button";
import { Card, CardHeader } from "@/ui/card";
import { cn } from "@/ui/cn";
import { DayPicker } from "@/ui/day-picker";
import { inputClass } from "@/ui/field";
import { HelpTip } from "@/ui/help-tip";
import { Segmented } from "@/ui/segmented";
import { Skeleton } from "@/ui/spinner";
import { Stat } from "@/ui/stat";
import { ErrorState } from "@/ui/states";

import {
  useAnalyticsDay,
  useAnalyticsMonth,
  useAnalyticsPlans,
  type AnalyticsMonth,
  type AnalyticsPlan,
  type AnalyticsSummary,
  type SignupRange,
} from "./analytics.queries";
import {
  addMonths,
  calendarWeeks,
  formatMonth,
  isMonth,
  latestRetention,
  RETENTION_HELP,
  retentionWords,
  signupBars,
  SIGNUP_RANGES,
} from "./overview-display";

const DIET_WORDS = { VEG: "Veg", EGG: "Egg", NON_VEG: "Non-veg" } as const;
/** Estimates in whole rupees (a price per meal rarely divides evenly). */
const estimate = (paise: number) => formatPaise(Math.round(paise / 100) * 100);

function Loading({ label, height = "h-64" }: { label: string; height?: string }) {
  return (
    <div aria-busy="true" aria-label={label}>
      <Skeleton className={cn(height, "rounded-2xl")} />
    </div>
  );
}

/** The old Overview's key numbers (also on Subscriptions): subscribers, plans, revenue, retention. */
export function KeyNumbers({
  summary,
  extra,
  thisMonth,
  onMonth,
}: {
  summary: AnalyticsSummary;
  extra?: React.ReactNode;
  /** With `onMonth`: the monthly numbers get a month picker (up to this month). */
  thisMonth?: string;
  onMonth?: (month: string) => void;
}) {
  const retention = latestRetention(summary.retention);
  const monthDetail =
    onMonth && thisMonth ? (
      <input
        type="month"
        aria-label="Month of the monthly numbers"
        value={summary.month}
        max={thisMonth}
        onChange={(event) =>
          isMonth(event.target.value) && event.target.value <= thisMonth && onMonth(event.target.value)
        }
        className="-ml-1 rounded-md bg-transparent px-1 text-[13px] text-ink-2 hover:bg-surface-muted"
      />
    ) : (
      formatMonth(summary.month)
    );
  return (
    <section
      aria-label="Key numbers"
      className={cn(
        "grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3",
        extra ? "2xl:grid-cols-6" : "lg:grid-cols-5",
      )}
    >
      <Stat
        label="Active subscribers"
        value={formatCount(summary.activeSubscribers)}
        detail={formatCountOf(summary.activePlans, "plan", "plans") + " running or starting"}
      />
      <Stat
        label="Total revenue"
        value={formatPaise(summary.revenuePaise)}
        detail={`All time · ${formatPaise(summary.activeRevenuePaise)} from plans running now`}
      />
      <Stat
        label="Monthly revenue"
        value={formatPaise(summary.thisMonth.revenuePaise)}
        detail={monthDetail}
      />
      <Stat
        label="New sign-ups"
        value={formatCount(summary.thisMonth.signups)}
        detail={`${formatMonth(summary.month, "short")} · ${formatCount(summary.thisMonth.cancellations)} cancelled`}
      />
      <Stat
        label={
          <>
            Retention
            <HelpTip topic="Retention">{RETENTION_HELP}</HelpTip>
          </>
        }
        value={retention ? formatBps(retention.retentionBps!) : "–"}
        detail={
          retention ? (
            <>
              {retentionWords(retention).outcome}
              <span className="block text-muted">
                Plans that ended in {formatMonth(retention.month, "name")}
                {retentionWords(retention).pending && ` · ${retentionWords(retention).pending}`}
              </span>
            </>
          ) : (
            "No plans have finished their 14 days yet"
          )
        }
      />
      {extra}
    </section>
  );
}

/** New plans per day or month (the old "New subscriptions" chart). */
export function SignupsCard({
  summary,
  today,
  range,
  onRange,
}: {
  summary: AnalyticsSummary;
  today: string;
  range: SignupRange;
  onRange: (range: SignupRange) => void;
}) {
  const bars = signupBars(range, summary.signups.points, today);
  const top = Math.max(1, ...bars.map((b) => b.count));
  const total = bars.reduce((sum, b) => sum + b.count, 0);
  const byMonth = range === "6m" || range === "1y";
  const label = (bucket: string) => (byMonth ? formatMonth(bucket) : formatDayLong(bucket));
  return (
    <Card aria-labelledby="signups" className="flex flex-col gap-4">
      <CardHeader id="signups" title="New subscriptions" aside={formatCount(total)} className="mb-0" />
      <Segmented
        label="Period"
        fill
        options={SIGNUP_RANGES.map((r) => ({ value: r.value, label: r.label }))}
        value={range}
        onChange={onRange}
      />
      <div
        role="img"
        aria-label={`${formatCountOf(total, "new plan")} in the last ${SIGNUP_RANGES.find((r) => r.value === range)!.label}`}
      >
        <div className="flex h-36 items-end gap-[3px]">
          {bars.map((b) => (
            <span
              key={b.bucket}
              title={`${label(b.bucket)}: ${formatCount(b.count)}`}
              className={cn(
                "min-w-0 flex-1 rounded-t-[4px]",
                b.count > 0 ? "bg-primary" : "bg-surface-muted",
              )}
              style={{ height: `${Math.max(4, (b.count / top) * 100)}%` }}
            />
          ))}
        </div>
        <div className="mt-1.5 flex justify-between text-xs text-muted">
          <span>{byMonth ? formatMonth(bars[0]!.bucket, "short") : formatDayLong(bars[0]!.bucket)}</span>
          <span>{byMonth ? formatMonth(bars.at(-1)!.bucket, "short") : "Today"}</span>
        </div>
      </div>
    </Card>
  );
}

/** Of the plans that ended each month, the share renewed within 14 days. */
export function RetentionCard({ summary }: { summary: AnalyticsSummary }) {
  return (
    <Card aria-labelledby="retention" className="flex flex-col gap-3">
      <CardHeader id="retention" title="Retention by month" className="mb-0" />
      <p className="text-[13px] text-muted">
        Of your customers whose plan ended in a month, how many bought another plan within 14 days. A plan
        still inside its 14 days counts once they're over.
      </p>
      {summary.retention.every((r) => r.ended === 0) ? (
        <p className="text-sm text-muted">No plans have ended in the last 6 months.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {summary.retention.map((r) => (
            <li key={r.month} className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-3">
              <span className="text-sm font-semibold">{formatMonth(r.month, "name")}</span>
              <span className="h-2.5 overflow-hidden rounded-full bg-surface-muted">
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${(r.retentionBps ?? 0) / 100}%` }}
                />
              </span>
              <span className="tabular text-sm">
                {r.ended === 0 ? (
                  <span className="text-muted">no plans ended</span>
                ) : (
                  <>
                    {r.retentionBps !== null && (
                      <span className="font-bold">{formatBps(r.retentionBps)} · </span>
                    )}
                    <span className="text-muted">
                      {r.retentionBps !== null &&
                        `${formatCount(r.renewed)} of ${formatCount(r.ended - r.pending)} bought again`}
                      {r.retentionBps !== null && r.pending > 0 && " · "}
                      {r.pending > 0 && `${formatCount(r.pending)} can still renew`}
                    </span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** The old Overview's "Today": a picked day's meals by slot and dish, and its estimated revenue. */
export function DayView({
  date,
  today,
  onDate,
}: {
  date: string;
  today: string;
  onDate: (day: string) => void;
}) {
  const { brand } = useBrand();
  const day = useAnalyticsDay(brand.id, date);
  const diets = (day.data?.dishes ?? []).reduce<Record<string, number>>(
    (sum, d) => ({ ...sum, [d.dietType]: (sum[d.dietType] ?? 0) + d.quantity }),
    {},
  );
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DayPicker date={date} today={today} onChange={onDate} />
        <Button asChild variant="secondary">
          <Link to={`${routeTo.orders(brand.id)}${date === today ? "" : `?date=${date}`}`}>Open orders</Link>
        </Button>
      </div>
      {day.isPending ? (
        <Loading label="Loading the day" />
      ) : day.isError ? (
        <ErrorState error={day.error} onRetry={() => void day.refetch()} />
      ) : (
        <>
          <section aria-label="The day" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <Stat
              label="Meals served"
              value={formatCount(day.data.served)}
              detail={formatDayLong(day.data.date)}
            />
            <Stat label="Paused" value={formatCount(day.data.paused)} />
            <Stat
              label="Missed"
              value={formatCount(day.data.missed)}
              detail="Not chosen in time, or not served"
            />
            <Stat
              label="Estimated revenue"
              value={estimate(day.data.estimatedRevenuePaise)}
              detail="Meals served × price per meal"
            />
          </section>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card aria-labelledby="by-slot">
              <CardHeader id="by-slot" title="By meal" />
              {day.data.slots.length === 0 ? (
                <p className="text-sm text-muted">No meals this day.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-bold tracking-wide text-muted uppercase">
                      <th className="py-2 font-bold">Meal</th>
                      <th className="py-2 text-right font-bold">Served</th>
                      <th className="py-2 text-right font-bold">Paused</th>
                      <th className="py-2 text-right font-bold">Missed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {day.data.slots.map((s) => (
                      <tr key={s.slotId} className="border-t border-border-subtle">
                        <td className="py-2.5 font-semibold">{s.slotName}</td>
                        <td className="tabular py-2.5 text-right font-bold">{formatCount(s.served)}</td>
                        <td className="tabular py-2.5 text-right">{formatCount(s.paused)}</td>
                        <td className="tabular py-2.5 text-right">{formatCount(s.missed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <Card aria-labelledby="by-dish" className="flex flex-col gap-3">
              <CardHeader id="by-dish" title="By dish" className="mb-0" />
              {day.data.dishes.length === 0 ? (
                <p className="text-sm text-muted">No dishes chosen.</p>
              ) : (
                <>
                  <ul aria-label="Meals by diet" className="flex flex-wrap gap-2">
                    {(["VEG", "EGG", "NON_VEG"] as const)
                      .filter((d) => diets[d])
                      .map((d) => (
                        <li
                          key={d}
                          className="flex items-center gap-1.5 rounded-lg bg-surface-muted px-2.5 py-1 text-sm"
                        >
                          <DietMark diet={d} className="size-3 border-[1.5px]" />
                          <span className="font-semibold">{DIET_WORDS[d]}</span>
                          <span className="tabular font-extrabold">{formatCount(diets[d]!)}</span>
                        </li>
                      ))}
                  </ul>
                  {day.data.withoutDish > 0 && (
                    <p className="text-[13px] text-muted">
                      {formatCountOf(day.data.withoutDish, "meal")} without a dish chosen yet.
                    </p>
                  )}
                  <ul className="flex flex-col">
                    {day.data.dishes.map((d) => (
                      <li
                        key={d.dishId}
                        className="flex items-center gap-2.5 border-t border-border-subtle py-2.5 first:border-t-0"
                      >
                        <DietMark diet={d.dietType} className="size-3 border-[1.5px]" />
                        <span className="grow text-sm font-semibold">{d.name}</span>
                        <span className="tabular text-sm font-bold">{formatCount(d.quantity)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          </div>
        </>
      )}
    </>
  );
}

function MonthPicker({
  month,
  thisMonth,
  onChange,
}: {
  month: string;
  thisMonth: string;
  onChange: (month: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="icon"
        aria-label="Month before"
        onClick={() => onChange(addMonths(month, -1))}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <input
        type="month"
        aria-label="Month"
        value={month}
        max={thisMonth}
        onChange={(event) =>
          isMonth(event.target.value) && event.target.value <= thisMonth && onChange(event.target.value)
        }
        className={cn(inputClass, "h-11 w-auto")}
      />
      <Button
        variant="secondary"
        size="icon"
        aria-label="Month after"
        disabled={month >= thisMonth}
        onClick={() => onChange(addMonths(month, 1))}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
      {month !== thisMonth && (
        <Button variant="quiet" onClick={() => onChange(thisMonth)}>
          This month
        </Button>
      )}
    </div>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The old Overview's "Monthly": revenue and plans of a month, and a calendar of meals served and paused. */
export function MonthView({
  month,
  today,
  onMonth,
  onDay,
}: {
  month: string;
  today: string;
  onMonth: (month: string) => void;
  onDay: (day: string) => void;
}) {
  const { brand } = useBrand();
  const stats = useAnalyticsMonth(brand.id, month);
  const byDay = new Map((stats.data?.days ?? []).map((d) => [d.date, d]));
  const top = Math.max(1, ...(stats.data?.days ?? []).map((d) => d.served));
  return (
    <>
      <MonthPicker month={month} thisMonth={today.slice(0, 7)} onChange={onMonth} />
      {stats.isPending ? (
        <Loading label="Loading the month" />
      ) : stats.isError ? (
        <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
      ) : (
        <>
          <section
            aria-label="The month"
            className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6"
          >
            <Stat
              label="Revenue"
              value={formatPaise(stats.data.totals.revenuePaise)}
              detail="Plans bought this month"
            />
            <Stat label="New plans" value={formatCount(stats.data.totals.signups)} />
            <Stat label="Cancelled" value={formatCount(stats.data.totals.cancellations)} />
            <Stat label="Meals served" value={formatCount(stats.data.served)} />
            <Stat label="Paused" value={formatCount(stats.data.paused)} />
            <Stat
              label="Estimated meal revenue"
              value={estimate(stats.data.estimatedRevenuePaise)}
              detail="Meals served × price per meal"
            />
          </section>
          <Card aria-labelledby="calendar" className="flex flex-col gap-3">
            <CardHeader id="calendar" title="Meal activity" aside={formatMonth(month)} className="mb-0" />
            <p className="text-[13px] text-muted">
              Meals served each day (paused under it). Pick a day to see it.
            </p>
            <div role="grid" aria-labelledby="calendar" className="grid grid-cols-7 gap-1 text-center">
              {WEEKDAYS.map((w) => (
                <span key={w} role="columnheader" className="py-1 text-xs font-bold text-muted uppercase">
                  {w}
                </span>
              ))}
              {calendarWeeks(month)
                .flat()
                .map((day, i) => {
                  if (!day) return <span key={`blank-${i}`} />;
                  const d = byDay.get(day);
                  const served = d?.served ?? 0;
                  return (
                    <button
                      key={day}
                      type="button"
                      role="gridcell"
                      onClick={() => onDay(day)}
                      aria-label={`${formatDayLong(day)}: ${formatCountOf(served, "meal")} served, ${formatCount(d?.paused ?? 0)} paused`}
                      className={cn(
                        "flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-lg p-1 text-xs transition-colors hover:ring-2 hover:ring-primary",
                        day === today && "ring-2 ring-primary/60",
                        served === 0 && "bg-surface-muted",
                      )}
                      style={{
                        backgroundColor:
                          served > 0
                            ? `color-mix(in oklab, var(--color-primary) ${Math.round(12 + (served / top) * 45)}%, transparent)`
                            : undefined,
                      }}
                    >
                      <span className="text-muted">{Number(day.slice(8))}</span>
                      <span className="tabular text-sm font-extrabold">
                        {served > 0 ? formatCount(served) : ""}
                      </span>
                      {(d?.paused ?? 0) > 0 && (
                        <span className="tabular text-[11px] text-muted">{d!.paused} p</span>
                      )}
                    </button>
                  );
                })}
            </div>
          </Card>
          <DailyRevenueCard month={month} days={stats.data.days} />
        </>
      )}
    </>
  );
}

/** Estimated revenue of each day of a month (the old "Daily revenue trend"). */
function DailyRevenueCard({ month, days }: { month: string; days: AnalyticsMonth["days"] }) {
  const byDay = new Map(days.map((d) => [d.date, d.estimatedRevenuePaise]));
  const all = calendarWeeks(month)
    .flat()
    .filter((d): d is string => d !== null);
  const top = Math.max(1, ...all.map((d) => byDay.get(d) ?? 0));
  const total = all.reduce((sum, d) => sum + (byDay.get(d) ?? 0), 0);
  return (
    <Card aria-labelledby="daily-revenue" className="flex flex-col gap-3">
      <CardHeader id="daily-revenue" title="Daily revenue" aside={estimate(total)} className="mb-0" />
      <p className="text-[13px] text-muted">
        Each day&apos;s meals served × their plan&apos;s price per meal.
      </p>
      <div role="img" aria-label={`Daily revenue in ${formatMonth(month)}: ${estimate(total)} in all`}>
        <div className="flex h-32 items-end gap-[3px]">
          {all.map((day) => {
            const paise = byDay.get(day) ?? 0;
            return (
              <span
                key={day}
                title={`${formatDayLong(day)}: ${estimate(paise)}`}
                className={cn(
                  "min-w-0 flex-1 rounded-t-[3px]",
                  paise > 0 ? "bg-primary" : "bg-surface-muted",
                )}
                style={{ height: `${Math.max(3, (paise / top) * 100)}%` }}
              />
            );
          })}
        </div>
        <div className="mt-1.5 flex justify-between text-xs text-muted">
          <span>1</span>
          <span>{all.length}</span>
        </div>
      </div>
    </Card>
  );
}

const PLAN_GRID = "xl:grid xl:grid-cols-[minmax(0,1.6fr)_6rem_6rem_8rem_8rem_minmax(0,1fr)_6rem]";

function PlanRow({ plan, total, topActive }: { plan: AnalyticsPlan; total: number; topActive: number }) {
  const share = total > 0 ? Math.round((plan.revenuePaise / total) * 10_000) : 0;
  const cells: [string, React.ReactNode][] = [
    ["Active", formatCount(plan.activeSubscribers)],
    ["Sold", formatCount(plan.subscriptions)],
    ["Revenue", formatPaise(plan.revenuePaise)],
    ["This month", formatPaise(plan.revenueThisMonthPaise)],
  ];
  return (
    <li className="flex flex-col gap-2 border-t border-border-subtle px-4 py-3.5 xl:px-5">
      <div className={cn(PLAN_GRID, "flex flex-col gap-2 xl:items-center xl:gap-4")}>
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="font-bold">{plan.planName}</span>
          <span
            className="h-1.5 w-full max-w-48 overflow-hidden rounded-full bg-surface-muted"
            aria-hidden="true"
          >
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${(plan.activeSubscribers / topActive) * 100}%` }}
            />
          </span>
        </span>
        {cells.map(([label, value]) => (
          <span key={label} className="tabular flex justify-between text-sm xl:block">
            <span className="text-muted xl:hidden">{label}</span>
            <span className="font-semibold">{value}</span>
          </span>
        ))}
        <span className="tabular flex justify-between text-sm xl:block">
          <span className="text-muted xl:hidden">Revenue share</span>
          <span className="font-semibold">{formatBps(share)}</span>
        </span>
        <span className="tabular flex justify-between text-sm xl:block">
          <span className="text-muted xl:hidden">Churn</span>
          <span
            className="font-semibold"
            title={`${formatCount(plan.ended)} ended in the last 3 months, ${formatCount(plan.renewed)} bought again${plan.pending ? `, ${formatCount(plan.pending)} can still renew` : ""}`}
          >
            {plan.churnBps === null ? "–" : formatBps(plan.churnBps)}
          </span>
        </span>
      </div>
    </li>
  );
}

/** The old Overview's "Plans": popularity, revenue share and churn per plan. */
export function PlansView() {
  const { brand } = useBrand();
  const plans = useAnalyticsPlans(brand.id);
  if (plans.isPending) return <Loading label="Loading plans" />;
  if (plans.isError) return <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />;
  if (plans.data.length === 0) return <p className="text-sm text-muted">No plans yet.</p>;
  const total = plans.data.reduce((sum, p) => sum + p.revenuePaise, 0);
  const topActive = Math.max(1, ...plans.data.map((p) => p.activeSubscribers));
  return (
    <>
      <Card className="overflow-hidden p-0">
        <div
          aria-hidden="true"
          className={cn(
            PLAN_GRID,
            "hidden gap-4 bg-surface-muted px-5 py-2.5 text-xs font-bold tracking-wide text-muted uppercase",
          )}
        >
          {["Plan", "Active", "Sold", "Revenue", "This month", "Share", "Churn"].map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <ul aria-label="Plans" className="[&>li:first-child]:border-t-0 xl:[&>li:first-child]:border-t">
          {plans.data.map((p) => (
            <PlanRow key={p.planId} plan={p} total={total} topActive={topActive} />
          ))}
        </ul>
      </Card>
      <p className="text-[13px] text-muted">
        Active: customers on the plan now. Sold: plans bought, all time. Churn: of the plans that ended in the
        last 3 months, the share not bought again within 14 days.
      </p>
    </>
  );
}

/** The latest plans bought (the old Overview's "Recent orders"). */
export function RecentOrdersCard() {
  const { brand } = useBrand();
  const list = useCustomerPlans(brand.id, {});
  const recent = (list.data?.pages[0]?.data ?? []).slice(0, 5);
  return (
    <Card aria-labelledby="recent" className="flex flex-col gap-3">
      <CardHeader
        id="recent"
        title="Recent orders"
        className="mb-0"
        aside={
          <Link to={routeTo.subscriptions(brand.id)} className="text-sm font-bold no-underline">
            See all
          </Link>
        }
      />
      {list.isPending ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : recent.length === 0 ? (
        <p className="text-sm text-muted">No plans bought yet.</p>
      ) : (
        <ul className="flex flex-col">
          {recent.map((s) => (
            <li key={s.id} className="border-t border-border-subtle first:border-t-0">
              <Link
                to={routeTo.subscription(brand.id, s.id)}
                className="flex items-center gap-3 py-2.5 text-ink no-underline hover:bg-surface-muted"
              >
                <span className="flex min-w-0 grow flex-col gap-0.5">
                  <span className="text-sm font-semibold">{displayName(s)}</span>
                  <span className="text-[13px] text-muted">
                    {s.planName} · {formatSlots(s.slots)} · starts {formatDay(s.startDate)}
                  </span>
                </span>
                <span className="tabular text-sm font-bold">
                  {s.source === "ADMIN" ? "Free" : formatPaise(s.pricePaise)}
                </span>
                <ChevronRight className="size-4 text-muted" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
